import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, ExternalLink, Lock, AlertCircle, MapPin, Briefcase, Calendar, Star, Send, Check, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * E7: Public Profile Page with OpenGraph/Twitter meta tags and oEmbed discovery
 * 
 * Public page at /id/{vett_id} that displays the full passport and verified records.
 * Includes meta tags for social sharing unfurls.
 */
export default function PublicProfile() {
  const { vettId } = useParams();
  
  const [profileData, setProfileData] = useState(null);
  const [fullData, setFullData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrivate, setIsPrivate] = useState(false);
  
  // Access request form state
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [accessForm, setAccessForm] = useState({
    requester_name: '',
    requester_email: '',
    requester_company: '',
    message: ''
  });
  const [submittingAccess, setSubmittingAccess] = useState(false);
  const [accessSubmitted, setAccessSubmitted] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Fetch full profile data from public lookup
        const fullResponse = await fetch(`${API_URL}/api/public/vett-id/${vettId}`);
        
        if (fullResponse.status === 404) {
          setError('Profile not found');
          return;
        }
        
        if (fullResponse.ok) {
          const data = await fullResponse.json();
          
          if (data.status === 'private') {
            setIsPrivate(true);
            return;
          }
          
          setFullData(data);
          setProfileData({
            title: `${data.first_name} ${data.last_initial}. - ${data.tier} Professional`,
            author_name: `${data.first_name} ${data.last_initial}.`,
            width: 320,
            height: 180
          });
        } else {
          // Fallback to oEmbed endpoint
          const response = await fetch(`${API_URL}/api/badge/${vettId}/oembed`);
          
          if (response.status === 404) {
            setError('Profile not found');
            return;
          }
          
          if (response.status === 403) {
            setIsPrivate(true);
            return;
          }
          
          if (!response.ok) {
            setError('Failed to load profile');
            return;
          }
          
          const data = await response.json();
          setProfileData(data);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    if (vettId) {
      fetchProfile();
    }
  }, [vettId]);

  // Handle access request submission
  const submitAccessRequest = async (e) => {
    e.preventDefault();
    
    if (!accessForm.requester_name || !accessForm.requester_email || !accessForm.requester_company) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingAccess(true);
    try {
      const response = await fetch(`${API_URL}/api/sharing/access-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vett_id: vettId,
          ...accessForm
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit request');
      }
      
      setAccessSubmitted(true);
      toast.success('Access request submitted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to submit access request');
    } finally {
      setSubmittingAccess(false);
    }
  };

  // Get tier color
  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'distinguished': return '#F0B429';
      case 'established': return '#00C9A7';
      case 'verified': return '#1B5AFF';
      default: return '#5B6472';
    }
  };

  // Get tier badge
  const getTierBadge = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'distinguished': return '✦';
      case 'established': return '●';
      case 'verified': return '✓';
      default: return '○';
    }
  };

  // Generate URLs
  const publicUrl = `${API_URL}/id/${vettId}`;
  const oembedUrl = `${API_URL}/api/badge/${vettId}/oembed`;
  const pngUrl = `${API_URL}/api/badge/${vettId}.png?style=card&theme=dark`;

  // Meta tag content
  const title = profileData?.title || `VETT Verified Profile - ${vettId}`;
  const description = profileData?.author_name 
    ? `View ${profileData.author_name}'s verified career credentials on VETT.`
    : 'View this verified professional profile on VETT.';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center">
        <div className="animate-pulse flex items-center gap-2 text-[#00C9A7]">
          <Shield className="w-8 h-8" />
          <span className="text-lg">Loading profile...</span>
        </div>
      </div>
    );
  }

  if (error || !vettId) {
    return (
      <>
        <Helmet>
          <title>Profile Not Found - VETT</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center p-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 max-w-md text-center">
            <AlertCircle className="w-12 h-12 text-[#1B5AFF] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0A0F1E] mb-2">Profile Not Found</h1>
            <p className="text-[#5B6472] mb-6">
              The VETT profile you&apos;re looking for doesn&apos;t exist or has been removed.
            </p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#00C9A7] hover:text-[#00E5BF]"
            >
              <ExternalLink className="w-4 h-4" />
              Go to VETT Homepage
            </Link>
          </Card>
        </div>
      </>
    );
  }

  if (isPrivate) {
    return (
      <>
        <Helmet>
          <title>Private Profile - VETT</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center p-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 max-w-md text-center">
            <Lock className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <h1 className="text-xl font-bold text-[#0A0F1E] mb-2">Private Profile</h1>
            <p className="text-[#5B6472] mb-6">
              This VETT profile is set to private. The owner has chosen not to share their credentials publicly.
            </p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-[#00C9A7] hover:text-[#00E5BF]"
            >
              <ExternalLink className="w-4 h-4" />
              Learn about VETT
            </Link>
          </Card>
        </div>
      </>
    );
  }

  const tierColor = getTierColor(fullData?.tier);
  const scoreOffset = 125.6 - ((fullData?.score || 0) / 100) * 125.6;

  return (
    <>
      {/* E7: OpenGraph, Twitter meta tags, and oEmbed discovery */}
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        
        {/* OpenGraph meta tags */}
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={publicUrl} />
        <meta property="og:image" content={pngUrl} />
        <meta property="og:image:width" content={profileData?.width || 320} />
        <meta property="og:image:height" content={profileData?.height || 180} />
        <meta property="og:site_name" content="VETT" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={pngUrl} />
        
        {/* oEmbed discovery link */}
        <link 
          rel="alternate" 
          type="application/json+oembed" 
          href={oembedUrl}
          title={title}
        />
      </Helmet>

      <div className="min-h-screen bg-[#F5F8FF]" data-testid="public-profile-page">
        {/* Header */}
        <header className="border-b border-[#E8EDF5] bg-[#FFFFFF]">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-[#00C9A7]" />
              <span className="text-xl font-bold text-[#0A0F1E]">VETT</span>
            </Link>
            <Link 
              to="/signup"
              className="text-sm px-4 py-2 bg-[#1B5AFF] text-white rounded-lg hover:bg-[#1450E6] transition-colors"
            >
              Create Your VETT ID
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            {/* Profile Card */}
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-2xl overflow-hidden mb-8">
              {/* Header with gradient */}
              <div className="h-24 bg-gradient-to-r from-[#1B5AFF]/20 via-[#00C9A7]/10 to-[#F0B429]/10"></div>
              
              {/* Profile Info */}
              <div className="px-8 pb-8 -mt-12">
                <div className="flex flex-col md:flex-row md:items-end gap-6 mb-8">
                  {/* Avatar/Score */}
                  <div className="relative">
                    <div 
                      className="w-24 h-24 rounded-2xl flex items-center justify-center text-3xl font-mono font-bold text-[#0A0F1E]"
                      style={{ background: `${tierColor}30` }}
                    >
                      {fullData?.first_name?.[0]}{fullData?.last_initial}
                    </div>
                    {/* Score badge */}
                    <div 
                      className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-[#0A0F1E] border-2 border-[#FFFFFF]"
                      style={{ background: tierColor }}
                    >
                      {fullData?.score || 0}
                    </div>
                  </div>
                  
                  {/* Name and headline */}
                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-[#0A0F1E] mb-1" data-testid="profile-name">
                      {fullData?.first_name} {fullData?.last_initial}.
                    </h1>
                    <p className="text-[#5B6472] text-lg">{fullData?.headline || 'Professional'}</p>
                  </div>
                  
                  {/* Tier badge */}
                  <div 
                    className="px-4 py-2 rounded-lg font-mono text-sm"
                    style={{ background: `${tierColor}20`, color: tierColor }}
                  >
                    {getTierBadge(fullData?.tier)} {fullData?.tier}
                  </div>
                </div>
                
                {/* VETT ID */}
                <div className="flex items-center gap-4 p-4 bg-[#F5F8FF] rounded-xl mb-6">
                  <Shield className="w-5 h-5 text-[#00C9A7]" />
                  <div>
                    <div className="text-xs text-[#6666AA] uppercase tracking-wider mb-1">VETT ID</div>
                    <div className="font-mono text-lg text-white" data-testid="profile-vett-id">{vettId}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-2 text-[#00C9A7] text-sm">
                    <Shield className="w-4 h-4" />
                    Verified
                  </div>
                </div>
                
                {/* Score Ring */}
                <div className="flex items-center gap-6 p-6 bg-[#F5F8FF] rounded-xl">
                  <div className="relative w-20 h-20">
                    <svg viewBox="0 0 44 44" className="w-full h-full">
                      <circle cx="22" cy="22" r="20" fill="none" stroke="#E8EDF5" strokeWidth="4"/>
                      <circle 
                        cx="22" cy="22" r="20" 
                        fill="none" 
                        stroke={tierColor} 
                        strokeWidth="4" 
                        strokeDasharray="125.6" 
                        strokeDashoffset={scoreOffset} 
                        strokeLinecap="round" 
                        transform="rotate(-90 22 22)"
                      />
                    </svg>
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-2xl font-bold text-[#0A0F1E] font-mono">
                      {fullData?.score || 0}
                    </span>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-white mb-1">VETT Score</div>
                    <div className="text-[#5B6472] text-sm">
                      Based on {fullData?.records_count || 0} verified record{fullData?.records_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            
            {/* Verified Records */}
            {fullData?.records && fullData.records.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-[#00C9A7]" />
                  Verified Career Records
                </h2>
                <div className="space-y-4">
                  {fullData.records.map((record, idx) => (
                    <Card key={idx} className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">{record.job_title}</h3>
                          <p className="text-[#5B6472] mb-2">{record.employer_name}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-[#6666AA]">
                            {record.department && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-4 h-4" />
                                {record.department}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {record.start_date?.slice(0, 7)} – {record.end_date?.slice(0, 7) || 'Present'}
                            </span>
                          </div>
                        </div>
                        {record.manager_rating && (
                          <div className="flex items-center gap-2 px-4 py-2 bg-[#F0B429]/10 rounded-lg">
                            <Star className="w-5 h-5 text-[#F0B429]" fill="#F0B429" />
                            <span className="text-lg font-semibold text-[#F0B429]">{record.manager_rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                      {record.manager_closing_statement && (
                        <div className="mt-4 pt-4 border-t border-[#E8EDF5]">
                          <p className="text-[#5B6472] italic">"{record.manager_closing_statement}"</p>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Selective visibility message with access request */}
            {fullData?.visibility === 'selective' && (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                {!showAccessForm && !accessSubmitted ? (
                  <div className="text-center">
                    <Lock className="w-8 h-8 text-[#5B6472] mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-[#0A0F1E] mb-2">Selective Visibility</h3>
                    <p className="text-[#5B6472] mb-4">
                      This professional has chosen selective visibility for their records. 
                      Request access to view full details.
                    </p>
                    <button 
                      onClick={() => setShowAccessForm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B5AFF] text-white rounded-lg hover:bg-[#1450E6] transition-colors"
                      data-testid="request-access-btn"
                    >
                      <Send className="w-4 h-4" />
                      Request Access
                    </button>
                  </div>
                ) : accessSubmitted ? (
                  <div className="text-center">
                    <div className="w-12 h-12 bg-[#00C9A7]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Check className="w-6 h-6 text-[#00C9A7]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#0A0F1E] mb-2">Request Submitted</h3>
                    <p className="text-[#5B6472]">
                      Your access request has been sent to the professional. 
                      You will be notified when they respond.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submitAccessRequest} data-testid="access-request-form">
                    <h3 className="text-lg font-semibold text-[#0A0F1E] mb-4">Request Access</h3>
                    <p className="text-[#5B6472] text-sm mb-4">
                      Introduce yourself to request access to this professional&apos;s full profile.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-[#5B6472] mb-1">Your Name *</label>
                        <input 
                          type="text"
                          value={accessForm.requester_name}
                          onChange={(e) => setAccessForm(prev => ({ ...prev, requester_name: e.target.value }))}
                          className="w-full px-4 py-3 bg-[#F5F8FF] border border-[#E8EDF5] rounded-lg text-white placeholder-[#6666AA] focus:outline-none focus:border-[#1B5AFF]"
                          placeholder="John Smith"
                          required
                          data-testid="access-name-input"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-[#5B6472] mb-1">Your Email *</label>
                        <input 
                          type="email"
                          value={accessForm.requester_email}
                          onChange={(e) => setAccessForm(prev => ({ ...prev, requester_email: e.target.value }))}
                          className="w-full px-4 py-3 bg-[#F5F8FF] border border-[#E8EDF5] rounded-lg text-white placeholder-[#6666AA] focus:outline-none focus:border-[#1B5AFF]"
                          placeholder="john@company.com"
                          required
                          data-testid="access-email-input"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-[#5B6472] mb-1">Company *</label>
                        <input 
                          type="text"
                          value={accessForm.requester_company}
                          onChange={(e) => setAccessForm(prev => ({ ...prev, requester_company: e.target.value }))}
                          className="w-full px-4 py-3 bg-[#F5F8FF] border border-[#E8EDF5] rounded-lg text-white placeholder-[#6666AA] focus:outline-none focus:border-[#1B5AFF]"
                          placeholder="Acme Corp"
                          required
                          data-testid="access-company-input"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-[#5B6472] mb-1">Message (optional)</label>
                        <textarea 
                          value={accessForm.message}
                          onChange={(e) => setAccessForm(prev => ({ ...prev, message: e.target.value }))}
                          className="w-full px-4 py-3 bg-[#F5F8FF] border border-[#E8EDF5] rounded-lg text-white placeholder-[#6666AA] focus:outline-none focus:border-[#1B5AFF] resize-none"
                          placeholder="Hi, I'm recruiting for a senior engineering role..."
                          rows={3}
                          data-testid="access-message-input"
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <button 
                        type="button"
                        onClick={() => setShowAccessForm(false)}
                        className="flex-1 px-4 py-3 bg-transparent border border-[#E8EDF5] text-[#5B6472] rounded-lg hover:bg-[#E8EDF5]/20 transition-colors"
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit"
                        disabled={submittingAccess}
                        className="flex-1 px-4 py-3 bg-[#1B5AFF] text-white rounded-lg hover:bg-[#1450E6] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="submit-access-request-btn"
                      >
                        {submittingAccess ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Request
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </Card>
            )}

            {/* Footer Info */}
            <div className="mt-8 text-center">
              <p className="text-[#6666AA] text-sm mb-4">
                This profile is verified by VETT - the trusted career credential platform.
              </p>
              <Link 
                to="/signup" 
                className="inline-flex items-center gap-2 text-[#00C9A7] hover:text-[#00E5BF] text-sm"
              >
                Create your own verified profile
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t border-[#E8EDF5] mt-auto">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-[#5B6472]">
                <Shield className="w-4 h-4" />
                <span className="text-sm">VETT - Verified Employment & Training Trust</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-[#6666AA]">
                <Link to="/privacy" className="hover:text-[#5B6472]">Privacy</Link>
                <Link to="/terms" className="hover:text-[#5B6472]">Terms</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
